import { useContext, useEffect, useRef } from "react";
import {
  DashboardSseContext,
  type DashboardSseHints,
} from "../context/DashboardSseContext.js";

export type { DashboardSseHints };

export interface DashboardSseCallbacks {
  onRevocation?: () => void;
  onSlaTick?: () => void;
  onIdentitiesBulk?: () => void;
  onRateQuota?: () => void;
  onLifecycle?: () => void;
}

/**
 * Reads SSE hints from {@link DashboardSseProvider} and optionally registers
 * refresh callbacks per event type. Does not open a second EventSource.
 */
export function useDashboardEventStream(callbacks?: DashboardSseCallbacks): DashboardSseHints {
  const ctx = useContext(DashboardSseContext);
  const cbRef = useRef(callbacks);

  useEffect(() => {
    cbRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    if (!ctx) return;
    const ur = ctx.registerOnRevocation(() => {
      cbRef.current?.onRevocation?.();
    });
    const us = ctx.registerOnSlaTick(() => {
      cbRef.current?.onSlaTick?.();
    });
    const ui = ctx.registerOnIdentitiesBulk(() => {
      cbRef.current?.onIdentitiesBulk?.();
    });
    const uq = ctx.registerOnRateQuota(() => {
      cbRef.current?.onRateQuota?.();
    });
    const ul = ctx.registerOnLifecycle(() => {
      cbRef.current?.onLifecycle?.();
    });
    return () => {
      ur();
      us();
      ui();
      uq();
      ul();
    };
  }, [ctx]);

  if (ctx) {
    return {
      revocationHint: ctx.revocationHint,
      slaBreachHint: ctx.slaBreachHint,
      rateQuotaHint: ctx.rateQuotaHint,
      lifecycleHint: ctx.lifecycleHint,
    };
  }

  return {
    revocationHint: null,
    slaBreachHint: null,
    rateQuotaHint: null,
    lifecycleHint: null,
  };
}
